import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, TablePagination } from "@mui/material";
import styles from "./productVisionTable.module.css";
import { downloadXls } from "../utils/exportToXls";
import StickyScrollShell from "../StickyScrollShell";
import { fetchProductVisionData } from "Samarth Module/api/ProductVisionApi";

const WEEK_COLUMN_WIDTH = 76;

let exportCache = { columns: [], columnPreferences: [], rows: [], filters: {}, totalRecords: 0 };
const PAGE_SIZE = 25;

const fetchPagesInBatches = async (totalPages, fetchPage, batchSize = 5) => {
    const pages = [];
    for (let firstPage = 1; firstPage <= totalPages; firstPage += batchSize) {
        const lastPage = Math.min(firstPage + batchSize - 1, totalPages);
        const batch = Array.from({ length: lastPage - firstPage + 1 }, (_, index) => fetchPage(firstPage + index));
        pages.push(...await Promise.all(batch));
    }
    return pages;
};

const WEEK_NUMBER_PATTERN = /\d+/;
const weekNumber = (key) => {
    const match = WEEK_NUMBER_PATTERN.exec(String(key));
    return match ? Number(match[0]) : 0;
};

const weekLabel = (key) => `Week ${weekNumber(key)}`;
const deriveWeeks = (rows) => {
    const weeks = new Set();
    rows.forEach((row) => {
        Object.keys(row?.weeklySchedule || {}).forEach((key) => weeks.add(key));
    });
    return Array.from(weeks).sort((a, b) => weekNumber(a) - weekNumber(b));
};

const fgLabel = (fg) => {
    if (!fg) return "-";
    const code = typeof fg.FG === "string" ? fg.FG.trim() : "";
    const name = typeof fg.name === "string" ? fg.name.trim() : "";
    return code || name || "-";
};

const activePvsOf = (row) => (Array.isArray(row?.activePvs) ? row.activePvs : []);

const displayValue = (value) => (value === "" || value === null || value === undefined ? "-" : value);

const BASE_COLUMNS = [
    {
        key: "site",
        label: "Site",
        width: 70,
        className: styles.siteColumn,
        value: (row) => row.site_code || "",
    },
    {
        key: "fg",
        label: "FG",
        width: 180,
        className: styles.fgColumn,
        value: (row) => fgLabel(row.fg),
    },
    {
        key: "count",
        label: "Count of Active PV's",
        width: 80,
        className: styles.countColumn,
        value: (row) => row.PV_Count ?? activePvsOf(row).length,
    },
    {
        key: "active",
        label: "Active PV's",
        width: 200,
        className: styles.activeColumn,
        value: (row) => activePvsOf(row).join(", "),
        cell: (row, rowIndex) => (
            <Box className={styles.tagList}>
                {activePvsOf(row).map((pv, index) => (
                    <span className={styles.tag} key={`${rowIndex}-${index}`}>
                        {pv}
                    </span>
                ))}
            </Box>
        ),
    },
    {
        key: "latestCreated",
        label: "Latest created PV's",
        width: 100,
        className: styles.metaColumn,
        value: (row) => row.Latest_created_pvs || "",
    },
    {
        key: "latestUsed",
        label: "Latest used PV's",
        width: 90,
        className: styles.metaColumn,
        value: (row) => row.Latest_used_pvs || "",
    },
];

const COLUMN_KEY_STRIP_PATTERN = /[\s_'’.-]+/g;
const WEEK_KEY_PATTERN = /(?:demand)?week(\d+)/;
const normalizedColumnKey = (value) => {
    const text = String(value || "").replaceAll(COLUMN_KEY_STRIP_PATTERN, "").toLowerCase();
    const week = WEEK_KEY_PATTERN.exec(text);
    if (week) return `week${week[1]}`;
    if (text.includes("count")) return "count";
    if (text.includes("created")) return "latestcreated";
    if (text.includes("used")) return "latestused";
    if (text.includes("active")) return "active";
    if (text.includes("site")) return "site";
    if (text.includes("finishedgood") || text.includes("fg")) return "fg";
    return text;
};

const preferenceFor = (preferences, key) => preferences.find((column) => (
    normalizedColumnKey(column?.key) === normalizedColumnKey(key)
    || normalizedColumnKey(column?.label) === normalizedColumnKey(key)
));

const isColumnVisible = (preferences, key) => {
    const preference = preferenceFor(preferences, key);
    return preference ? preference.visibility !== false && preference.checked !== false : true;
};

const displayOrderFor = (preferences, key) => preferenceFor(preferences, key)?.DisplayOrder ?? Number.MAX_SAFE_INTEGER;

function ProductVisionTable({ filters, clearFilters, columnPreferences }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const filtersKey = JSON.stringify(filters || {});
    const clearFiltersKey = String(clearFilters);

    useEffect(() => {
        setPage(1);
    }, [filtersKey, clearFiltersKey]);

    useEffect(() => {
        let cancelled = false;

        const loadData = async () => {
            setLoading(true);
            setError("");
            try {
                const data = await fetchProductVisionData({
                    filters: JSON.parse(filtersKey),
                    clearFilters: clearFilters,
                    pagination: { page, pageSize: PAGE_SIZE },
                });
                if (!cancelled) {
                    setRows(data.rows);
                    setTotalRecords(data.totalRecords);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error("Unable to load Product Version data:", err);
                    setError("Unable to load Product Version data. Please try again.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadData();
        return () => {
            cancelled = true;
        };
    }, [filtersKey, clearFiltersKey, page]);

    const weeks = useMemo(() => deriveWeeks(rows), [rows]);
    const visibleColumns = useMemo(() => BASE_COLUMNS
        .filter((column) => isColumnVisible(columnPreferences, column.key))
        .sort((a, b) => displayOrderFor(columnPreferences, a.key) - displayOrderFor(columnPreferences, b.key)),
        [columnPreferences]);

    const visibleWeeks = useMemo(() => weeks
        .filter((week) => isColumnVisible(columnPreferences, weekLabel(week)))
        .sort((a, b) => displayOrderFor(columnPreferences, weekLabel(a)) - displayOrderFor(columnPreferences, weekLabel(b))),
        [weeks, columnPreferences]);

    const columnLefts = useMemo(() => {
        let running = 0;
        return visibleColumns.map((column) => {
            const left = running;
            running += column.width;
            return left;
        });
    }, [visibleColumns]);
    const frozenWidth = visibleColumns.reduce((total, column) => total + column.width, 0);

    useEffect(() => {
        exportCache = { columns: visibleColumns, columnPreferences, rows, filters: JSON.parse(filtersKey), totalRecords };
    }, [visibleColumns, columnPreferences, rows, filtersKey, totalRecords]);

    if (loading) {
        return (
            <Box className={styles.stateShell}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (error) {
        return <Box className={styles.stateShell}>{error}</Box>;
    }

    if (!rows.length) {
        return <Box className={styles.stateShell}>No Product Version data available.</Box>;
    }

    return (
        <>
            <StickyScrollShell frozenWidth={frozenWidth}>
                <table className={styles.table}>
                    <colgroup>
                        {visibleColumns.map((column) => (
                            <col key={`col-${column.key}`} style={{ width: column.width }} />
                        ))}
                        {visibleWeeks.map((week) => (
                            <col key={`col-${week}`} style={{ width: WEEK_COLUMN_WIDTH }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {visibleColumns.map((column, index) => (
                                <th
                                    key={column.key}
                                    className={`${styles.primaryHeader} ${column.className} ${styles.stickyHeaderCell} ${index === visibleColumns.length - 1 ? styles.stickyEdgeHeaderCell : ""}`}
                                    style={{ left: columnLefts[index] }}
                                >
                                    {column.label}
                                </th>
                            ))}
                            {visibleWeeks.map((week) => (
                                <th className={`${styles.primaryHeader} ${styles.weekColumn}`} key={week}>{weekLabel(week)}</th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((row, rowIndex) => {
                            const weeklySchedule = row.weeklySchedule || {};
                            return (
                                <tr key={row.id ? `${row.id}-${rowIndex}` : `pv-row-${rowIndex}`}>
                                    {visibleColumns.map((column, index) => (
                                        <td
                                            key={column.key}
                                            className={`${styles.textCell} ${styles.stickyBodyCell} ${index === visibleColumns.length - 1 ? styles.stickyEdgeCell : ""}`}
                                            style={{ left: columnLefts[index] }}
                                        >
                                            {column.cell ? column.cell(row, rowIndex) : displayValue(column.value(row))}
                                        </td>
                                    ))}
                                    {visibleWeeks.map((week) => (
                                        <td key={`pv-${rowIndex}-${week}`} className={styles.weekCell}>
                                            {weeklySchedule[week] || "-"}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </StickyScrollShell>
            <TablePagination
                className={styles.pagination}
                component="div"
                count={totalRecords}
                page={page - 1}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[]}
                onPageChange={(_, nextPage) => setPage(nextPage + 1)}
                labelDisplayedRows={({ from, to, count }) => `${from} – ${to} of ${count}`}
                sx={{
                    "& .MuiTablePagination-toolbar": { minHeight: 30, marginTop: '10px', },
                    "& .MuiTablePagination-displayedRows": { fontSize: 14, margin: 0 },
                    "& .MuiTablePagination-actions": { marginLeft: 2, paddingBottom: 0 },
                    "& .MuiIconButton-root": { padding: 0 },
                }}
            />
        </>
    );
}

ProductVisionTable.propTypes = {
    filters: PropTypes.objectOf(PropTypes.any),
    clearFilters: PropTypes.bool,
    columnPreferences: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            visibility: PropTypes.bool,
            checked: PropTypes.bool,
            DisplayOrder: PropTypes.number,
        }),
    ),
};

ProductVisionTable.defaultProps = {
    filters: {},
    clearFilters: false,
    columnPreferences: [],
};

export const exportProductVisionTable = async () => {
    const { columns, columnPreferences, rows, filters, totalRecords } = exportCache;
    if (!rows.length) return false;

    let exportRowsData;
    try {
        const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
        const pagePayloads = await fetchPagesInBatches(totalPages, (page) => fetchProductVisionData({
            filters,
            pagination: { page, pageSize: PAGE_SIZE },
        }));
        exportRowsData = pagePayloads.flatMap((data) => data.rows || []);
    } catch (error) {
        console.error("Unable to export all Product Vision pages:", error.message);
        return false;
    }

    const exportWeeks = deriveWeeks(exportRowsData)
        .filter((week) => isColumnVisible(columnPreferences, weekLabel(week)))
        .sort((a, b) => displayOrderFor(columnPreferences, weekLabel(a)) - displayOrderFor(columnPreferences, weekLabel(b)));

    const headers = [
        ...columns.map((column) => column.label),
        ...exportWeeks.map(weekLabel),
    ];
    const exportRows = exportRowsData.map((row) => {
        const weeklySchedule = row.weeklySchedule || {};
        return [
            ...columns.map((column) => column.value(row)),
            ...exportWeeks.map((week) => weeklySchedule[week] || ""),
        ];
    });
    const currentDate = new Date();
    const formattedCurrentDate = [
        String(currentDate.getDate()).padStart(2, "0"),
        String(currentDate.getMonth() + 1).padStart(2, "0"),
        String(currentDate.getFullYear()),
    ].join("-");

    downloadXls(headers, exportRows, `PV_RM_Analytics_${formattedCurrentDate}.xls`);
    return true;
};

export default ProductVisionTable;